import React, { useEffect, useMemo } from "react";
import { buildStatusOptions } from "./assessmentFilters";
import { injectAssessmentChrome } from "./assessmentChrome";
import MultiPickDropdown from "./MultiPickDropdown";

export { MultiPickDropdown };

/**
 * Filter bar for the riu_admin assessment templates page. Matches the
 * company_admin chrome (`.kpi-input` for inputs, white card with rounded
 * corners). Status is a click-out dropdown that lets the user pick any
 * number of values from the rows on the page — no hardcoded enum.
 */
const AssessmentFilterBar = ({
  rows = [],
  statusField = "status_key",
  search = "",
  onSearchChange,
  searchPlaceholder = "Search…",
  statusFilter = [],
  onStatusFilterChange,
  statusPlaceholder = "Filter by status…",
  showStatus = true,
  dateRange = { from: "", to: "" },
  onDateRangeChange,
  dateLabel = "Updated",
  extraFilters = null,
}) => {
  useEffect(() => {
    injectAssessmentChrome();
  }, []);

  const statusOptions = useMemo(
    () => (showStatus ? buildStatusOptions(rows, statusField) : []),
    [rows, statusField, showStatus]
  );

  const hasAny =
    Boolean(search) ||
    (showStatus && statusFilter.length > 0) ||
    Boolean(dateRange?.from || dateRange?.to);

  const clearAll = () => {
    onSearchChange?.("");
    if (showStatus) onStatusFilterChange?.([]);
    onDateRangeChange?.({ from: "", to: "" });
  };

  return (
    <div
      style={{
        display: "flex",
        gap: 8,
        alignItems: "center",
        flexWrap: "wrap",
        padding: "12px 16px",
        background: "var(--surface)",
        border: "1px solid var(--rule)",
        borderRadius: 12,
        marginBottom: 12,
      }}
    >
      <div
        style={{
          position: "relative",
          flex: "1 1 220px",
          minWidth: 180,
          maxWidth: 320,
        }}
      >
        <i
          className="fas fa-search"
          style={{
            position: "absolute",
            left: 12,
            top: "50%",
            transform: "translateY(-50%)",
            color: "var(--ink-4)",
            fontSize: 13,
            pointerEvents: "none",
            zIndex: 1,
          }}
          aria-hidden
        />
        <input
          className="kpi-input"
          style={{ paddingLeft: 32, paddingRight: search ? 32 : 12, height: 36 }}
          placeholder={searchPlaceholder}
          value={search}
          onChange={(e) => onSearchChange?.(e.target.value)}
          aria-label="Search"
        />
        {search ? (
          <button
            type="button"
            onClick={() => onSearchChange?.("")}
            style={{
              position: "absolute",
              right: 8,
              top: "50%",
              transform: "translateY(-50%)",
              border: "none",
              background: "transparent",
              cursor: "pointer",
              fontSize: 18,
              color: "var(--ink-4)",
              lineHeight: 1,
              padding: 0,
            }}
            aria-label="Clear search"
          >
            ×
          </button>
        ) : null}
      </div>

      {showStatus ? (
        <MultiPickDropdown
          options={statusOptions}
          selected={statusFilter}
          onChange={onStatusFilterChange}
          placeholder={statusPlaceholder}
        />
      ) : null}

      {extraFilters}

      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 6,
          fontSize: 11,
          color: "var(--ink-4)",
          textTransform: "uppercase",
          letterSpacing: "0.05em",
          fontWeight: 700,
        }}
      >
        {dateLabel}
      </div>
      <input
        type="date"
        className="kpi-input"
        style={{ width: 150, height: 36, padding: "0px 12px 14px 12px" }}
        value={dateRange?.from || ""}
        onChange={(e) =>
          onDateRangeChange?.({ ...dateRange, from: e.target.value })
        }
        aria-label={`${dateLabel} from`}
      />
      <span style={{ color: "var(--ink-4)" }}>→</span>
      <input
        type="date"
        className="kpi-input"
        style={{ width: 150, height: 36, padding: "0px 12px 14px 12px" }}
        value={dateRange?.to || ""}
        onChange={(e) =>
          onDateRangeChange?.({ ...dateRange, to: e.target.value })
        }
        aria-label={`${dateLabel} to`}
      />

      {hasAny ? (
        <button
          type="button"
          onClick={clearAll}
          className="kpi-btn-secondary"
          style={{
            marginLeft: "auto",
            padding: "6px 12px",
            height: 36,
            fontSize: 11.5,
          }}
        >
          Clear all
        </button>
      ) : null}
    </div>
  );
};

export default AssessmentFilterBar;
